import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ErroApi, SemRede, api } from '../dados/api.js'
import { banco, removerInspecaoLocal, type InspecaoLocal } from '../dados/banco.js'
import { sincronizando } from '../dados/sincronizacao.js'

/// Apagar uma inspeção.
///
/// São dois casos diferentes e a tela precisa dizer qual é:
///
/// - **Nunca enviada** — existe só neste aparelho. Some na hora, sem rede, e não
///   há como recuperar.
/// - **Já enviada** — o servidor precisa saber. Sem rede não dá para apagar, e
///   fingir que deu apagaria só a cópia local: a inspeção voltaria na próxima
///   sincronização e a pessoa acharia que o sistema tem vida própria.
///
/// No servidor a exclusão é lógica: a linha fica marcada com data de exclusão e
/// some das listas, mas continua no banco. Uma interdição apagada por engano
/// precisa ter volta, e a trilha de auditoria registra quem apagou.

/// Fica no cabeçalho da inspeção, ao lado do "Voltar": visível sem rolar, e longe
/// do "Finalizar", que mora no rodapé. Já esteve no fim do formulário — seguro
/// contra toque acidental, mas num PTE de dezenas de perguntas ninguém o achava.
export function ExcluirInspecao({ inspecao }: { inspecao: InspecaoLocal }) {
  const [confirmando, setConfirmando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [ocupado, setOcupado] = useState(false)
  const navegar = useNavigate()

  // Pode existir no servidor: veio de lá, já teve resposta, ou já saiu ao menos uma
  // vez — a criação pode ter chegado e a resposta se perdido. Sem este último caso,
  // excluir logo depois de criar no 4G apagava só a cópia daqui, e a inspeção
  // voltava na descida seguinte, somente leitura, e continuava no servidor.
  const noServidor =
    inspecao.remota || inspecao.versaoServidor !== null || inspecao.tentouEnvio === true

  async function apagar() {
    setOcupado(true)
    setErro(null)
    // No meio de um envio, excluir abriria uma corrida: o servidor responderia "não
    // encontrada" à exclusão e criaria a inspeção um instante depois.
    const agora = await banco.inspecoes.get(inspecao.id)
    if (agora?.sincronizacao === 'ENVIANDO' && sincronizando()) {
      setErro('Esta inspeção está sendo enviada agora. Espere alguns segundos e tente de novo.')
      setOcupado(false)
      return
    }
    try {
      if (noServidor) {
        try {
          await api.excluirInspecao(inspecao.id)
        } catch (e) {
          // 404: o servidor já não tem a inspeção — outra pessoa, outro aparelho
          // ou um clique anterior a excluiu. O que se pediu já está feito; falta
          // só tirar a cópia daqui.
          if (!(e instanceof ErroApi && e.status === 404)) throw e
        }
      }
      await removerInspecaoLocal(inspecao.id)
      navegar('/', { replace: true })
    } catch (e) {
      setErro(traduzir(e, noServidor))
      setOcupado(false)
    }
  }

  if (!confirmando) {
    return (
      <button
        onClick={() => setConfirmando(true)}
        className="inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-sm font-medium text-alta hover:bg-alta/5"
      >
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
          <path d="M4 7h16M10 11v6M14 11v6M6 7l1 12a2 2 0 002 2h6a2 2 0 002-2l1-12M9 7V4h6v3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        Excluir
      </button>
    )
  }

  return (
    // basis-full: no cabeçalho, a confirmação desce para uma linha própria em vez
    // de se espremer ao lado do "Voltar".
    <div className="basis-full rounded-lg border border-alta/30 bg-alta/5 p-3">
      <p className="font-semibold text-alta">Excluir {inspecao.codigo}?</p>
      <p className="mt-1 text-sm text-slate-700">
        {noServidor
          ? 'Esta inspeção já foi enviada. Ela sai das listas para todo mundo, mas fica guardada ' +
            'no servidor e o registro de quem excluiu vai para a auditoria.'
          : 'Esta inspeção existe só neste aparelho e nunca foi enviada. Apagar é definitivo — ' +
            'não há cópia em lugar nenhum.'}
      </p>
      {inspecao.fotos.length > 0 && (
        <p className="mt-1 text-sm text-slate-700">
          {inspecao.fotos.length === 1
            ? '1 foto vai junto.'
            : `${inspecao.fotos.length} fotos vão junto.`}
        </p>
      )}

      {erro && (
        <p className="mt-2 rounded bg-white px-3 py-2 text-sm text-alta" role="alert">
          {erro}
        </p>
      )}

      <div className="mt-3 flex gap-2">
        <button onClick={() => void apagar()} disabled={ocupado} className="botao-perigo flex-1">
          {ocupado ? 'Excluindo…' : 'Excluir'}
        </button>
        <button
          onClick={() => {
            setConfirmando(false)
            setErro(null)
          }}
          disabled={ocupado}
          className="botao-neutro flex-1"
        >
          Cancelar
        </button>
      </div>
    </div>
  )
}

function traduzir(e: unknown, noServidor: boolean): string {
  if (e instanceof SemRede) {
    return noServidor
      ? 'Sem conexão. Esta inspeção já está no servidor, então a exclusão precisa de internet — ' +
          'senão ela voltaria na próxima sincronização.'
      : 'Não foi possível apagar. Tente de novo.'
  }
  if (e instanceof ErroApi) return e.message
  return (e as Error)?.message ?? 'Não foi possível apagar.'
}
